/**
 * Tenant Resolution
 *
 * Determines the business ID from the URL:
 * - On *.webeatery.menu: extracts subdomain, calls API to resolve business ID
 * - On localhost/other: falls back to VITE_BUSINESS_ID
 */

const PRODUCTION_DOMAIN = 'webeatery.menu'
const MARKETING_SITE = 'https://webeatery.app'
const TENANT_API_TIMEOUT = 5000

export interface TenantResult {
  type: 'resolved' | 'fallback'
  businessId: string
}

export interface TenantError {
  type: 'redirect' | 'error'
  message?: string
}

/**
 * Check if we're running on the production domain (webeatery.menu).
 */
function isProductionDomain(hostname: string): boolean {
  return hostname === PRODUCTION_DOMAIN || hostname.endsWith(`.${PRODUCTION_DOMAIN}`)
}

/**
 * Handle URL routing before tenant resolution.
 * Returns true if a redirect was performed (caller should stop).
 */
function handleRouting(hostname: string, pathname: string): boolean {
  // Root domain or www → redirect to marketing site
  if (hostname === PRODUCTION_DOMAIN || hostname === `www.${PRODUCTION_DOMAIN}`) {
    const pathSegment = pathname.split('/').filter(Boolean)[0]
    if (pathSegment) {
      // webeatery.menu/joes → joes.webeatery.menu
      window.location.href = `https://${pathSegment}.${PRODUCTION_DOMAIN}`
    } else {
      // webeatery.menu → webeatery.app
      window.location.href = MARKETING_SITE
    }
    return true
  }
  return false
}

/**
 * Extract the subdomain from a *.webeatery.menu hostname.
 * Returns null if not on the production domain.
 */
function extractSubdomain(hostname: string): string | null {
  if (!hostname.endsWith(`.${PRODUCTION_DOMAIN}`)) return null
  const subdomain = hostname.slice(0, -(PRODUCTION_DOMAIN.length + 1))
  if (!subdomain || subdomain === 'www') return null
  return subdomain.toLowerCase()
}

/**
 * Call the tenant API to resolve a subdomain to a business ID.
 */
async function fetchTenantBusinessId(
  subdomain: string,
  apiBaseUrl: string
): Promise<string> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), TENANT_API_TIMEOUT)

  try {
    const response = await fetch(`${apiBaseUrl}/tenants/${subdomain}`, {
      signal: controller.signal,
    })

    if (response.status === 404) {
      window.location.href = MARKETING_SITE
      throw new Error('redirect')
    }

    if (!response.ok) {
      throw new Error(`Tenant API returned ${response.status}`)
    }

    const data = await response.json()
    return data.business_id
  } finally {
    clearTimeout(timeoutId)
  }
}

/**
 * Resolve the current tenant.
 *
 * - On production domain: extracts subdomain, calls API
 * - On other domains: falls back to VITE_BUSINESS_ID
 *
 * Returns a TenantResult on success.
 * Throws with a TenantError-shaped object on failure.
 * May redirect (and never return) for invalid/missing tenants.
 */
export async function resolveTenant(): Promise<TenantResult> {
  const hostname = window.location.hostname
  const pathname = window.location.pathname

  // Handle routing (redirects for root domain, www, path-based URLs)
  if (handleRouting(hostname, pathname)) {
    // Redirect was triggered — this promise never resolves
    return new Promise(() => {})
  }

  // Production domain: resolve via API
  if (isProductionDomain(hostname)) {
    const subdomain = extractSubdomain(hostname)
    if (!subdomain) {
      window.location.href = MARKETING_SITE
      return new Promise(() => {})
    }

    const apiBaseUrl = import.meta.env.VITE_FLASK_BASE_URL || 'https://api.thalione.tech'
    const businessId = await fetchTenantBusinessId(subdomain, apiBaseUrl)
    return { type: 'resolved', businessId }
  }

  // Local dev / other domains: use env var
  const envBusinessId = import.meta.env.VITE_BUSINESS_ID
  if (!envBusinessId) {
    throw new Error('VITE_BUSINESS_ID not set and not on production domain')
  }
  return { type: 'fallback', businessId: envBusinessId }
}
